import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/components/auth/AuthProvider";

export const useEventInterest = (eventId: string) => {
  const { isAuthenticated } = useAuth();
  const queryClient = useQueryClient();

  const { data: hasInterest, isLoading } = useQuery({
    queryKey: ['event-interest', eventId],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return false;

      const { data, error } = await supabase
        .from('event_interest')
        .select('id')
        .eq('event_id', eventId)
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) throw error;
      return !!data;
    },
    enabled: isAuthenticated,
  });

  const registerInterest = useMutation({
    mutationFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { error } = await supabase
        .from('event_interest')
        .insert({ event_id: eventId, user_id: user.id });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.setQueryData(['event-interest', eventId], true);
    },
  });

  const cancelInterest = useMutation({
    mutationFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { error } = await supabase
        .from('event_interest')
        .delete()
        .eq('event_id', eventId)
        .eq('user_id', user.id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.setQueryData(['event-interest', eventId], false);
    },
  });

  return { hasInterest: hasInterest ?? false, registerInterest, cancelInterest, isLoading };
};
